import {DEFAULT_DATASET} from '@apps-common/geocorn-sdk/api/datasets';
import {
  deleteFeature,
  getFeatureByReference,
  updateFeature,
} from '@apps-common/geocorn-sdk/api/features';
import {getFeatureReference} from '@apps-common/geocorn-sdk/api/util';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {queryKeys as collectionsQueryKeys} from './collections.js';
import {graneroPager, removeEmpty} from './util.js';
import {getHashFromId} from '@app/util/granero';

// ToDo: Promote to geocorn-sdk once stable
// Uses custom "pager" to be able to get all features
const getFeatures = (collectionId, options) => {
  const {dataset = DEFAULT_DATASET, params, ...otherOptions} = options || {};

  const config = {
    url: `/ogc/${dataset}/collections/${collectionId}/items/`,
    params: {...params},
    ...otherOptions,
  };

  if (!config.params.limit) {
    config.params.limit = 100; // default so we can bail early on incomplete pages
  }

  if (!config.maxItems) {
    config.maxItems = config.params.limit;
  }

  return graneroPager(config, 'features', config.each);
};

export const queryKeys = {
  all: ['features'],
  lists: () => [...queryKeys.all, 'list'],
  list: params => [...queryKeys.lists(), params],
  details: () => [...queryKeys.all, 'details'],
  detail: id => [...queryKeys.all, 'details', id],
};

export const useGetFeatures = (
  {collectionId, params, ...options} = {},
  queryOptions = {}
) => {
  const queryParams = removeEmpty({
    //ToDo: Bring back once CSS-532 is resolved
    // _view: 'basic',
    '_x-lock': true,
    ...params,
  });
  return useQuery({
    queryKey: queryKeys.list({
      collectionId: getHashFromId(collectionId),
      ...queryParams,
      ...options,
    }),
    queryFn: () => getFeatures(collectionId, {params: queryParams, ...options}),
    ...queryOptions,
  });
};

export const useGetFeatureByReference = (
  {reference} = {},
  queryOptions = {}
) => {
  return useQuery({
    queryKey: queryKeys.list(reference),
    queryFn: () => getFeatureByReference(reference),
    ...queryOptions,
  });
};

export const useUpdateFeature = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({feature}) => updateFeature(feature),
    ...queryOptions,
    onSuccess: (data, args) => {
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
  });
};

export const useDeleteFeature = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({feature}) => {
      const featureRef = getFeatureReference(feature);
      return deleteFeature(featureRef);
    },
    ...queryOptions,
    onSuccess: (data, args) => {
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});
      queryClient.invalidateQueries({queryKey: collectionsQueryKeys.lists()});
    },
  });
};
